/*
 *
 * Copyright (c) 2025 Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Alexander Orlov ("CONFIDENTIAL INFORMATION").
 *
 * Author: Alexander Orlov
 * Aegis AO Soft
 *
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Users,
  UserPlus,
  LogOut,
  Menu,
  X,
  Home,
  FileText,
  Settings,
  Palette,
  AlertCircle,
  CheckCircle,
  Car,
  ChevronUp,
  ChevronDown,
  Phone,
  Mail,
  Facebook,
  Youtube,
  Instagram,
  CreditCard,
  MessageSquare,
  Truck,
  Building,
  Shield,
  BarChart2,
  Activity, FileChartColumn, Calendar, BarChart3,
  Receipt, List, LayoutDashboard,
  Pin, PinOff,
  BookOpen,
  MapPin,
  Globe,
  Plug,
  SlidersHorizontal,
  AlertTriangle,
  Server,
  DollarSign,
  Pencil,  Gavel,
  History,
  Zap,
} from 'lucide-react';
import { cn, accountDisplayName, accountInitials, formatDateTime } from '@/utils';
import { useAuthStore, useAppStore } from '@/store';
import { LanguageSelector, SandboxIndicator } from '@/components/common';
import { HelpPanel } from '@/components/help/HelpPanel';
import { HelpButton } from '@/components/help/HelpButton';
import { EditOwnerProfileModal } from '@/components/modals/EditOwnerProfileModal';
import { useTranslation } from 'react-i18next';
import { tollProviderApi, OwnerTollAccountDTO } from '@/api/tollProvider';
import { ownerTollBalanceApi } from '@/api/ownerTollBalance';
import { ownerBookingPluginsApi, OwnerBookingPluginRental } from '@/api/ownerBookingPlugins';
import { gpsApi } from '@/api/gpsTracking';
import { systemApi, HostVersion } from '@/api/system';
import type { StripeAccountStatus } from '@/shared/components/owners/types';
import '../layout/Footer.css';

interface LayoutProps {
  children: React.ReactNode;
}

const navigation = [
  { nameKey: 'nav.allVehicles', href: '/all-vehicles', icon: List },
  { nameKey: 'nav.rates', href: '/rates', icon: DollarSign },
  { nameKey: 'nav.pricingSeasons', href: '/pricing-seasons', icon: Calendar },
  { nameKey: 'nav.carSharing', href: '/car-sharing', icon: Car },
  { nameKey: 'nav.commercialCars', href: '/commercial-cars', icon: Truck },
  { nameKey: 'nav.licensePlates', href: '/license-plates', icon: FileText },
  { nameKey: 'nav.gpsTracking', href: '/gps-tracking', icon: MapPin },
];

const bookingsNav = [
  { nameKey: 'nav.bookings', href: '/bookings', icon: Calendar },
];

const paymentsNav = [
  { nameKey: 'nav.charges', href: '/payments/charges', icon: Receipt },
  { nameKey: 'nav.tolls', href: '/payments/tolls', icon: List },
  { nameKey: 'nav.violations', href: '/payments/violations', icon: AlertTriangle },
  { nameKey: 'nav.failedPayments', href: '/payments/failed', icon: AlertCircle },
  { nameKey: 'nav.successfulPayments', href: '/payments/successful', icon: CheckCircle },
  { nameKey: 'nav.stripeDashboard', href: '/payments/stripe-dashboard', icon: BarChart2 },
  // Debts the reminders could not collect, waiting on a decision to refer them. Hidden by default:
  // the whole feature is off unless an owner deliberately switches it on.
  { nameKey: 'nav.collections', href: '/payments/collections', icon: Gavel },
];

const disputesNav = [
  { nameKey: 'nav.disputes', href: '/disputes', icon: Shield },
];

const smsNav = [
  { nameKey: 'nav.smsLog', href: '/sms-log', icon: MessageSquare },
  { nameKey: 'nav.messages', href: '/messages', icon: Mail },
];

const renterNav = [
  { nameKey: 'nav.smsLog', href: '/sms-log', icon: MessageSquare },
  { nameKey: 'nav.tolls', href: '/renter-tolls', icon: List },
  { nameKey: 'nav.stripeCards', href: '/driver-cards', icon: CreditCard },
];

// Map nav nameKey (e.g. 'nav.carSharing') to GUI page key (e.g. 'carSharing')
const navKeyToGuiKey = (nameKey: string): string => nameKey.replace('nav.', '');

// Android robot — monochrome via currentColor, used by the footer link (desktop)
// and by the mobile menu entry (the footer itself is hidden below 768px).
const AndroidIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M8.6 3.2L9.9 5.1M15.4 3.2L14.1 5.1" stroke="currentColor" strokeWidth="1" strokeLinecap="round" fill="none" />
    <path fillRule="evenodd" clipRule="evenodd" d="M6 10a6 6 0 0 1 12 0H6ZM9.5 7.5a.72.72 0 1 0 0-1.44.72.72 0 0 0 0 1.44ZM14.5 7.5a.72.72 0 1 0 0-1.44.72.72 0 0 0 0 1.44Z" />
    <rect x="6" y="10.7" width="12" height="7.9" rx="1.5" />
    <rect x="3.2" y="11.1" width="2.3" height="5.9" rx="1.15" />
    <rect x="18.5" y="11.1" width="2.3" height="5.9" rx="1.15" />
    <rect x="8.7" y="18.2" width="2.3" height="3.6" rx="1.15" />
    <rect x="13" y="18.2" width="2.3" height="3.6" rx="1.15" />
  </svg>
);

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, logout, isAdmin, isOwner, isPartner } = useAuthStore();
  const showSidebar = isOwner || isAdmin || isPartner;
  const { selectedOwner, setSelectedOwner, reset: resetAppStore, guiSettings } = useAppStore();

  // GPS Tracking is only offered when the owner has at least one car with a
  // tracker. Re-checked when the active owner changes (admin owner switch).
  const [hasGpsVehicle, setHasGpsVehicle] = useState(false);
  useEffect(() => {
    let cancelled = false;
    gpsApi
      .trackers()
      .then((list) => { if (!cancelled) setHasGpsVehicle(list.length > 0); })
      .catch(() => { if (!cancelled) setHasGpsVehicle(false); });
    return () => { cancelled = true; };
  }, [selectedOwner?.id, user?.id]);

  // Filter nav items based on logged-in user's GUI page visibility settings
  // Admin always sees all pages regardless of settings
  const isPageVisible = (nameKey: string): boolean => {
    // Admin always sees every page, no exceptions.
    if (isAdmin) return true;
    // GPS Tracking hides itself when the (selected) owner has no GPS-equipped car.
    if (nameKey === 'nav.gpsTracking' && !hasGpsVehicle) return false;
    if (!guiSettings?.pages) return true; // no config = all visible
    const key = navKeyToGuiKey(nameKey);
    const page = guiSettings.pages[key];
    return page?.visible !== false; // default visible if not configured
  };
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bookingsExpanded, setBookingsExpanded] = useState(false);
  const [vehiclesExpanded, setVehiclesExpanded] = useState(false);
  const [paymentsExpanded, setPaymentsExpanded] = useState(false);
  const [reportsExpanded, setReportsExpanded] = useState(false);
  const [smsExpanded, setToolsExpanded] = useState(false);
  const [usersExpanded, setUsersExpanded] = useState(false);
  const [adminExpanded, setAdminExpanded] = useState(false);
  const [tollProvidersExpanded, setTollProvidersExpanded] = useState(false);
  const [partnershipExpanded, setPartnershipExpanded] = useState(false);
  const [companyExpanded, setCompanyExpanded] = useState(false);
  const [billingExpanded, setBillingExpanded] = useState(false);
  const [tollAccounts, setTollAccounts] = useState<OwnerTollAccountDTO[]>([]);
  const [tollAccountsVersion, setTollAccountsVersion] = useState(0);
  // Configured P2P (rental) plugins for the current owner — used to render per-plugin
  // pending-bookings sub-items in the Bookings nav section.
  const [p2pPlugins, setP2pPlugins] = useState<OwnerBookingPluginRental[]>([]);
  const [stripeStatus, setStripeStatus] = useState<StripeAccountStatus | null>(null);
  const [showCopyright, setShowCopyright] = useState(false);
  // Backend build shown in the About/copyright dialog — lets a deploy be confirmed at a glance.
  const [hostVersion, setHostVersion] = useState<HostVersion | null>(null);
  useEffect(() => {
    if (!showCopyright || hostVersion) return;
    systemApi.getHostVersion().then(setHostVersion).catch(() => { /* non-critical */ });
  }, [showCopyright, hostVersion]);

  // Poll Stripe Connect status for owners (and admin when impersonating another owner).
  // Used to flag the "Stripe" shortcut in the header with a red error badge when onboarding
  // is incomplete / disabled / missing required info.
  const shouldCheckStripe = isOwner || (isAdmin && !!selectedOwner && selectedOwner.id !== user?.id);
  useEffect(() => {
    if (!shouldCheckStripe) {
      setStripeStatus(null);
      return;
    }
    let cancelled = false;
    ownerTollBalanceApi.getSelfStripeStatus()
      .then((s) => { if (!cancelled) setStripeStatus(s); })
      .catch(() => { if (!cancelled) setStripeStatus(null); });
    return () => { cancelled = true; };
  }, [shouldCheckStripe, selectedOwner?.id, user?.id]);

  // Whether WE charge the customer (our Stripe) for at least one category. The owner's Stripe Connect account
  // only matters then — when nobody / the booking plugin collects, we don't nag the owner to set it up.
  const [collectsViaPlatform, setCollectsViaPlatform] = useState(true);
  useEffect(() => {
    const ownerId = selectedOwner?.id || user?.id;
    if (!shouldCheckStripe || !ownerId) { setCollectsViaPlatform(true); return; }
    let cancelled = false;
    ownerBookingPluginsApi.getPaymentCollector(ownerId)
      .then((info) => {
        if (cancelled) return;
        const cats = ['booking', 'toll', 'violation'] as const;
        const we = cats.some((c) => (info?.collectors?.[c]?.effective
          ?? (c === 'booking' ? info?.collector : 'platform')) === 'platform');
        setCollectsViaPlatform(we);
      })
      .catch(() => { if (!cancelled) setCollectsViaPlatform(true); });
    return () => { cancelled = true; };
  }, [shouldCheckStripe, selectedOwner?.id, user?.id]);

  const stripeIssueMessage: string | null = (() => {
    if (!shouldCheckStripe || !stripeStatus) return null;
    // Don't nag about the Stripe Connect account when we collect nothing via our Stripe.
    if (!collectsViaPlatform) return null;
    if (!stripeStatus.isConnected) {
      return t('stripeIssue.notConnected', 'Stripe account not set up yet');
    }
    const disabled = stripeStatus.requirements?.disabledReason;
    if (disabled) {
      return t('stripeIssue.disabled', 'Stripe account disabled: {{reason}}', { reason: disabled });
    }
    const pastDue = stripeStatus.requirements?.pastDue ?? [];
    if (pastDue.length > 0) {
      return t('stripeIssue.pastDue', 'Action required on Stripe account');
    }
    if (!stripeStatus.onboardingComplete || !stripeStatus.chargesEnabled || !stripeStatus.payoutsEnabled) {
      return t('stripeIssue.incomplete', 'Stripe onboarding incomplete');
    }
    return null;
  })();
  const hasStripeIssue = stripeIssueMessage !== null;
  const [headerPinned, setHeaderPinned] = useState(() => {
    const saved = localStorage.getItem('layout-header-pinned');
    return saved !== null ? saved === 'true' : true;
  });
  const [footerPinned, setFooterPinned] = useState(() => {
    const saved = localStorage.getItem('layout-footer-pinned');
    return saved !== null ? saved === 'true' : true;
  });
  const currentYear = new Date().getFullYear();

  const toggleHeaderPinned = () => {
    setHeaderPinned(prev => {
      localStorage.setItem('layout-header-pinned', String(!prev));
      return !prev;
    });
  };
  const toggleFooterPinned = () => {
    setFooterPinned(prev => {
      localStorage.setItem('layout-footer-pinned', String(!prev));
      return !prev;
    });
  };

  // Build users items based on roles
  const usersItems = [
    { nameKey: 'nav.users', href: '/users', icon: Users },                // always visible
    ...((isAdmin || isOwner) ? [
      { nameKey: 'nav.createUser', href: `/create-user${user?.id ? `?OwnerId=${user.id}` : ''}`, icon: UserPlus }, // admin or owner
    ] : []),
  ];

  // Partnership section — admin or partner only
  const partnershipItems = (isAdmin || isPartner) ? [
    { nameKey: 'nav.createOwner', href: `/create-owner${user?.id ? `?OwnerId=${user.id}` : ''}`, icon: UserPlus },
    { nameKey: 'nav.partners', href: '/partners', icon: Building },
  ] : [];

  // Build reports items based on roles
  const getReportsItems = () => [
    // Owner and Admin reports
    ...((isAdmin || isOwner) ? [
      { nameKey: 'nav.accountStatementReport', href: '/reports/account-statement', icon: FileChartColumn },
      { nameKey: 'nav.annualReport', href: '/reports/annual', icon: Calendar },
    ] : []),
    // Admin-only platform reports
    ...(isAdmin ? [
      { nameKey: 'nav.platformAccountStatementReport', href: '/admin/reports/account-statement', icon: BarChart3 },
      { nameKey: 'nav.platformAnnualReport', href: '/admin/reports/annual', icon: FileText },
    ] : []),
  ];

  // Company section — the owner's own company config (locations, settings, storefront). Admin or owner.
  // Partners also get the Settings entry (to manage their owners' Company Settings, e.g. Registration Fee),
  // but not Locations/Design.
  const companyItems = [
    ...((isAdmin || isOwner) ? [
      { nameKey: 'nav.locations', href: '/locations', icon: MapPin },       // manage pickup locations
    ] : []),
    ...((isAdmin || isOwner || isPartner) ? [
      { nameKey: 'nav.settings', href: '/settings', icon: Settings },       // admin, owner, or partner
    ] : []),
    ...((isAdmin || isOwner) ? [
      { nameKey: 'nav.design', href: '/design', icon: Palette },            // storefront design
    ] : []),
  ];

  // Billing section — the owner's fee invoices (Variant B). Admin or owner.
  const billingItems = (isAdmin || isOwner) ? [
    { nameKey: 'nav.invoicesToPay', href: '/billing/invoices', icon: FileText },
  ] : [];

  // Build administration items based on roles (matching aa-web pattern)
  const administrationItems = [
    ...((isAdmin || isOwner) ? [
      { nameKey: isAdmin ? 'nav.owners' : 'nav.tollDistributions', href: '/owners', icon: Building },  // admin or owner
    ] : []),
    // Settings page — admin or partner (partners manage their owners' settings tabs).
    ...((isAdmin || isPartner) ? [
      { nameKey: 'nav.adminSettings', href: '/admin/settings', icon: Settings },
    ] : []),
    ...(isAdmin ? [
      { nameKey: 'nav.platformStripeDashboard', href: '/admin/platform-stripe-dashboard', icon: Activity },
      { nameKey: 'nav.commonLocations', href: '/admin/locations', icon: Globe },
      { nameKey: 'nav.plugins', href: '/admin/plugins', icon: Plug },
      { nameKey: 'nav.pluginsSettings', href: '/admin/plugins-settings', icon: SlidersHorizontal },
      { nameKey: 'nav.scrapeWorker', href: '/admin/scrape-worker', icon: Server },
      { nameKey: 'nav.platformInvoices', href: '/admin/platform-invoices', icon: FileText },
      // Admin-only on purpose: the change history names every operator and their IP across all owners.
      { nameKey: 'nav.changeLog', href: '/admin/change-log', icon: History },
      // Booking platforms (HQ, Fleetwire, …) connected through the Partner API: tokens, partner account, owner defaults.
      { nameKey: 'nav.integrations', href: '/admin/integrations', icon: Plug },
    ] : []),
  ];

  // Only the links the current user may actually see, per section. A section header
  // (and every link under it) is rendered only when this list is non-empty — so closing
  // a page removes its now-empty section and all menu items pointing at it.
  const visibleVehicles = navigation.filter(i => isPageVisible(i.nameKey));
  const visiblePayments = [...paymentsNav, ...disputesNav].filter(i => isPageVisible(i.nameKey));
  const visibleSms = smsNav.filter(i => isPageVisible(i.nameKey));
  const visibleCompany = companyItems.filter(i => isPageVisible(i.nameKey));
  const visibleBilling = billingItems.filter(i => isPageVisible(i.nameKey));
  const visibleUsers = usersItems.filter(i => isPageVisible(i.nameKey));
  const visiblePartnership = partnershipItems.filter(i => isPageVisible(i.nameKey));
  const visibleReports = getReportsItems().filter(i => isPageVisible(i.nameKey));
  const visibleAdministration = administrationItems.filter(i => isPageVisible(i.nameKey));
  // Bookings: closing the Bookings page also closes every booking link (incl. P2P plugins).
  const bookingsVisible = isPageVisible('nav.bookings');

  // Accordion: opening one section collapses the others (matching aa-web)
  const collapseAll = () => { setBookingsExpanded(false); setVehiclesExpanded(false); setPaymentsExpanded(false); setReportsExpanded(false); setToolsExpanded(false); setUsersExpanded(false); setAdminExpanded(false); setTollProvidersExpanded(false); setPartnershipExpanded(false); setCompanyExpanded(false); setBillingExpanded(false); };
  const toggleBookings = () => {
    const next = !bookingsExpanded;
    if (next) collapseAll();
    setBookingsExpanded(next);
  };
  const toggleVehicles = () => {
    const next = !vehiclesExpanded;
    if (next) collapseAll();
    setVehiclesExpanded(next);
  };
  const togglePayments = () => {
    const next = !paymentsExpanded;
    if (next) collapseAll();
    setPaymentsExpanded(next);
  };
  const toggleReports = () => {
    const next = !reportsExpanded;
    if (next) collapseAll();
    setReportsExpanded(next);
  };
  const toggleSms = () => {
    const next = !smsExpanded;
    if (next) collapseAll();
    setToolsExpanded(next);
  };
  const toggleUsers = () => {
    const next = !usersExpanded;
    if (next) collapseAll();
    setUsersExpanded(next);
  };
  const toggleAdmin = () => {
    const next = !adminExpanded;
    if (next) collapseAll();
    setAdminExpanded(next);
  };
  const toggleTollProviders = () => {
    const next = !tollProvidersExpanded;
    if (next) collapseAll();
    setTollProvidersExpanded(next);
  };
  const togglePartnership = () => {
    const next = !partnershipExpanded;
    if (next) collapseAll();
    setPartnershipExpanded(next);
  };
  const toggleCompany = () => {
    const next = !companyExpanded;
    if (next) collapseAll();
    setCompanyExpanded(next);
  };
  const toggleBilling = () => {
    const next = !billingExpanded;
    if (next) collapseAll();
    setBillingExpanded(next);
  };

  // Reload sidebar toll accounts when any page adds/removes an account
  useEffect(() => {
    const handler = () => setTollAccountsVersion(v => v + 1);
    window.addEventListener('toll-accounts-changed', handler);
    return () => window.removeEventListener('toll-accounts-changed', handler);
  }, []);

  // Load toll accounts for sidebar
  const effectiveOwnerId = selectedOwner?.id || user?.id;
  useEffect(() => {
    if (!effectiveOwnerId || (!isOwner && !isAdmin)) return;
    tollProviderApi.getOwnerAccounts(effectiveOwnerId)
      .then(accounts => setTollAccounts(accounts))
      .catch(() => setTollAccounts([]));

    ownerBookingPluginsApi.list(effectiveOwnerId)
      .then(all => setP2pPlugins(
        all.filter((p): p is OwnerBookingPluginRental => p.family === 'rental' && p.isConfigured)
      ))
      .catch(() => setP2pPlugins([]));
  }, [effectiveOwnerId, isOwner, isAdmin, tollAccountsVersion]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Scroll page (and main content area) to the top on every route change so each page —
  // Dashboard in particular — always opens from the start, banner and all.
  useEffect(() => {
    window.scrollTo(0, 0);
    document.querySelector('.main-content')?.scrollTo?.(0, 0);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    resetAppStore();
    navigate('/login');
  };


  // ─── Edit Owner Profile (full modal) ───────────────────────────────
  // Owners have company/business-location properties, not the driver-license
  // workflow — so this opens the owner-specific editor, not the driver modal.
  const [showOwnerProfileModal, setShowOwnerProfileModal] = useState(false);

  const openProfileModal = useCallback(() => {
    if (user) {
      setShowOwnerProfileModal(true);
      setMobileMenuOpen(false);
    }
  }, [user]);

  return (
    <div className="shared-layout">
      {/* Header */}
      {/* Header Pin (independent). While the header is collapsed its help/logout actions are
          unreachable, so they reappear as tiny icons left of the pin. */}
      <div className="pin-floating-group pin-floating-group-top">
        {!headerPinned && (
          <>
            {/* Dev only: the collapsed header hides its own sandbox mark, so it reappears here,
                left of the language chip. */}
            <SandboxIndicator variant="pin" />
            <LanguageSelector pin />
            <HelpButton variant="pin" />
            <button
              onClick={handleLogout}
              className="pin-floating pin-action pin-action-logout"
              title={t('nav.logout')}
              aria-label={t('nav.logout')}
            >
              <LogOut />
            </button>
          </>
        )}
        <button
          onClick={toggleHeaderPinned}
          className={cn('pin-floating', !headerPinned && 'pin-action pin-action-pin')}
          title={headerPinned ? t('nav.unpinHeader', 'Unpin header') : t('nav.pinHeader', 'Pin header')}
        >
          {headerPinned ? <Pin /> : <PinOff />}
        </button>
      </div>

      <header className={cn('fixed-header', !headerPinned && 'header-unpinned')}>
        <div className="header-container">
          <Link to="/" className="header-logo">
            <img src="/images/myeztoll_logo.png" alt="My E-Z Toll" />
          </Link>
          <div className="header-actions">
            <Link to="/" className="header-nav-link flex items-center gap-1 mr-4">
              <LayoutDashboard className="h-4 w-4" />
              {t('nav.dashboard')}
            </Link>
            <HelpButton variant="header" />
            {/* Dev only. */}
            <SandboxIndicator variant="header" />
            <LanguageSelector compact className="mr-4" />
            <button onClick={handleLogout} className="logout-btn">
              <LogOut className="h-4 w-4 mr-2" />
              {t('nav.logout')}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Header */}
      <div className="mobile-header">
        <button
          className="mobile-menu-toggle"
          onClick={() => { if (!mobileMenuOpen) collapseAll(); setMobileMenuOpen(!mobileMenuOpen); }}
        >
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
        <div className="mobile-user-info" onDoubleClick={openProfileModal}>
          <span className="mobile-user-name">
            {accountDisplayName(user)}
          </span>
        </div>
        {/* The desktop header is display:none below 768px, so mobile needs its own sandbox mark
            and help trigger. */}
        <SandboxIndicator variant="mobile" />
        <HelpButton variant="mobile" />
      </div>


      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)}>
          <div className="mobile-menu" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-menu-header">
              <div className="mobile-user-info-full" onDoubleClick={openProfileModal}>
                <div className="mobile-user-avatar">
                  <span>{accountInitials(user)}</span>
                </div>
                <div className="mobile-user-details">
                  <h3>{accountDisplayName(user)}</h3>
                  <p>{user?.email}</p>
                </div>
                <button
                  type="button"
                  className="user-edit-btn"
                  onClick={openProfileModal}
                  title={t('ownerProfile.editTitle', 'Edit Owner Profile')}
                  aria-label={t('ownerProfile.editTitle', 'Edit Owner Profile')}
                >
                  <Pencil className="h-4 w-4" />
                </button>
              </div>
              {showSidebar && (
                <div className="mobile-stripe-wrapper">
                  {isPageVisible('nav.myStripe') && (
                  <Link
                    to="/stripe-cards"
                    className="mobile-stripe-link"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <CreditCard className="h-4 w-4" />
                    {t('nav.myStripe')}
                    {hasStripeIssue && (
                      <AlertCircle className="h-5 w-5 ml-1 text-red-600" aria-label={stripeIssueMessage ?? ''} />
                    )}
                  </Link>
                  )}
                  {hasStripeIssue && (
                    <div
                      role="alert"
                      className="mt-2 flex items-start gap-2 rounded-md border-2 border-red-500 bg-red-50 px-3 py-2 shadow-sm"
                    >
                      <AlertCircle className="h-5 w-5 flex-shrink-0 text-red-600 mt-0.5" />
                      <div className="text-sm font-semibold text-red-700 leading-snug">
                        {stripeIssueMessage}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Selected owner indicator for admin/partner (mobile, only when viewing another owner) */}
            {(isAdmin || isPartner) && selectedOwner && selectedOwner.id !== user?.id && (
              <div className="selected-owner-banner mobile">
                <div className="selected-owner-info">
                  <span className="selected-owner-label">{t('nav.viewingAsOwner')}</span>
                  <span className="selected-owner-name">
                    {selectedOwner.firstName} {selectedOwner.lastName}
                  </span>
                  <span className="selected-owner-email">{selectedOwner.email}</span>
                </div>
                <button
                  className="selected-owner-clear"
                  onClick={() => {
                    if (user) {
                      setSelectedOwner({
                        id: user.id,
                        email: user.email,
                        firstName: user.firstName,
                        lastName: user.lastName,
                      });
                    }
                  }}
                  title={t('nav.clearSelectedOwner')}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}

            <div className="mobile-menu-nav">
              {!showSidebar ? (
                /* Renter: only SMS Log and Tolls */
                renterNav.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={cn('mobile-nav-item', location.pathname === item.href && 'active')}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <item.icon className="h-5 w-5" />
                    <span>{t(item.nameKey)}</span>
                  </Link>
                ))
              ) : (
                /* Owner / Admin: full navigation */
                <>
              {isPageVisible('nav.dashboard') && (
                <Link
                  to="/"
                  className={cn('mobile-nav-item', location.pathname === '/' && 'active')}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Home className="h-5 w-5" />
                  <span>{t('nav.dashboard')}</span>
                </Link>
              )}

              {companyItems.length > 0 && (
                <>
                  <div className="mobile-nav-section-header" onClick={toggleCompany}>
                    <span>{t('nav.sections.company')}</span>
                    {companyExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </div>
                  {companyExpanded && companyItems.filter(i => isPageVisible(i.nameKey)).map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={cn('mobile-nav-item', location.pathname === item.href && 'active')}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <item.icon className="h-5 w-5" />
                      <span>{t(item.nameKey)}</span>
                    </Link>
                  ))}
                </>
              )}

              {billingItems.length > 0 && (
                <>
                  <div className="mobile-nav-section-header" onClick={toggleBilling}>
                    <span>{t('nav.sections.billing')}</span>
                    {billingExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </div>
                  {billingExpanded && billingItems.filter(i => isPageVisible(i.nameKey)).map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={cn('mobile-nav-item', location.pathname === item.href && 'active')}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <item.icon className="h-5 w-5" />
                      <span>{t(item.nameKey)}</span>
                    </Link>
                  ))}
                </>
              )}

              <div className="mobile-nav-section-header" onClick={toggleBookings}>
                <span>{t('nav.sections.bookings')}</span>
                {bookingsExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </div>
              {bookingsExpanded && bookingsNav.filter(i => isPageVisible(i.nameKey)).map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn('mobile-nav-item', location.pathname === item.href && 'active')}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{t(item.nameKey)}</span>
                </Link>
              ))}
              {bookingsExpanded && isPageVisible('nav.p2pBookings') && p2pPlugins.map((p) => {
                const href = `/p2p-bookings/${p.platformId}`;
                return (
                  <Link
                    key={p.platformId}
                    to={href}
                    className={cn('mobile-nav-item', location.pathname === href && 'active')}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Calendar className="h-5 w-5" />
                    <span>{p.platformName}</span>
                  </Link>
                );
              })}

              <div className="mobile-nav-section-header" onClick={toggleVehicles}>
                <span>{t('nav.sections.vehicles')}</span>
                {vehiclesExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </div>
              {vehiclesExpanded && navigation.filter(i => isPageVisible(i.nameKey)).map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn('mobile-nav-item', location.pathname === item.href && 'active')}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{t(item.nameKey)}</span>
                </Link>
              ))}

              {usersItems.length > 0 && (
                <>
                  <div className="mobile-nav-section-header" onClick={toggleUsers}>
                    <span>{t('nav.sections.users')}</span>
                    {usersExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </div>
                  {usersExpanded && usersItems.filter(i => isPageVisible(i.nameKey)).map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={cn('mobile-nav-item', location.pathname === item.href && 'active')}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <item.icon className="h-5 w-5" />
                      <span>{t(item.nameKey)}</span>
                    </Link>
                  ))}
                </>
              )}

              {partnershipItems.length > 0 && (
                <>
                  <div className="mobile-nav-section-header" onClick={togglePartnership}>
                    <span>{t('nav.sections.partnership')}</span>
                    {partnershipExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </div>
                  {partnershipExpanded && partnershipItems.filter(i => isPageVisible(i.nameKey)).map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={cn('mobile-nav-item', location.pathname === item.href && 'active')}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <item.icon className="h-5 w-5" />
                      <span>{t(item.nameKey)}</span>
                    </Link>
                  ))}
                </>
              )}

              <div className="mobile-nav-section-header" onClick={togglePayments}>
                <span>{t('nav.sections.payments')}</span>
                {paymentsExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </div>
              {paymentsExpanded && (
                <>
                  {paymentsNav.filter(i => isPageVisible(i.nameKey)).map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={cn('mobile-nav-item', location.pathname === item.href && 'active')}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <item.icon className="h-5 w-5" />
                      <span>{t(item.nameKey)}</span>
                    </Link>
                  ))}
                  {disputesNav.filter(i => isPageVisible(i.nameKey)).map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={cn('mobile-nav-item', location.pathname === item.href && 'active')}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <item.icon className="h-5 w-5" />
                      <span>{t(item.nameKey)}</span>
                    </Link>
                  ))}
                </>
              )}

              {isAdmin && (
                <>
                  <div className="mobile-nav-section-header" onClick={toggleReports}>
                    <span>{t('nav.sections.reports')}</span>
                    {reportsExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </div>
                  {reportsExpanded && getReportsItems().filter(i => isPageVisible(i.nameKey)).map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={cn('mobile-nav-item', location.pathname === item.href && 'active')}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <item.icon className="h-5 w-5" />
                      <span>{t(item.nameKey)}</span>
                    </Link>
                  ))}
                </>
              )}

              {isAdmin && (
                <>
                  <div className="mobile-nav-section-header" onClick={toggleSms}>
                    <span>{t('nav.sections.sms')}</span>
                    {smsExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </div>
                  {smsExpanded && smsNav.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={cn('mobile-nav-item', location.pathname === item.href && 'active')}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <item.icon className="h-5 w-5" />
                      <span>{t(item.nameKey)}</span>
                    </Link>
                  ))}
                </>
              )}

              {administrationItems.length > 0 && (
                <>
                  <div className="mobile-nav-section-header" onClick={toggleAdmin}>
                    <span>{t('nav.sections.administration')}</span>
                    {adminExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </div>
                  {adminExpanded && administrationItems.filter(i => isPageVisible(i.nameKey)).map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={cn('mobile-nav-item', location.pathname === item.href && 'active')}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <item.icon className="h-5 w-5" />
                      <span>{t(item.nameKey)}</span>
                    </Link>
                  ))}
                </>
              )}

                </>
              )}

              {/* Mobile app + guides — these live in the footer on desktop, but the
                  footer is hidden below 768px, so surface them here too. The APKs are
                  installed by the owner side only; renters don't need them. */}
              {showSidebar && (
                <Link
                  to="/download"
                  className={cn('mobile-nav-item', location.pathname === '/download' && 'active')}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <AndroidIcon className="h-5 w-5" />
                  <span>{t('footer.apps.mobileApp')}</span>
                </Link>
              )}
              {isPageVisible('nav.guides') && (
              <Link
                to="/guides"
                className={cn('mobile-nav-item', location.pathname === '/guides' && 'active')}
                onClick={() => setMobileMenuOpen(false)}
              >
                <BookOpen className="h-5 w-5" />
                <span>{t('download.guides.title')}</span>
              </Link>
              )}
            </div>

            <div className="mobile-menu-footer">
              <div className="px-4 py-3 border-t border-gray-200">
                <LanguageSelector className="w-full" />
              </div>
              <button onClick={handleLogout} className="mobile-logout-btn">
                <LogOut className="h-5 w-5" />
                <span>{t('nav.logout')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content Area */}
      <div className={cn('content-area', !headerPinned && 'content-no-header', !footerPinned && 'content-no-footer')}>
        {/* Sidebar - Desktop Only */}
        <aside className={cn('sidebar', !headerPinned && 'sidebar-no-header', !footerPinned && 'sidebar-no-footer')}>
          <div className="sidebar-header">
            <div className="user-info" onDoubleClick={openProfileModal}>
              <div className="user-avatar">
                <span>{accountInitials(user)}</span>
              </div>
              <div className="user-details">
                <h3 title={accountDisplayName(user)}>{accountDisplayName(user)}</h3>
                <p className="user-email">{user?.email}</p>
              </div>
              {/* The double-click on the block opens the same editor, but nothing on screen says so —
                  this is how an owner finds their way to the company name. */}
              <button
                type="button"
                className="user-edit-btn"
                onClick={openProfileModal}
                title={t('ownerProfile.editTitle', 'Edit Owner Profile')}
                aria-label={t('ownerProfile.editTitle', 'Edit Owner Profile')}
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            </div>
            {showSidebar && (
              <div className="my-stripe-wrapper">
                {isPageVisible('nav.myStripe') && (
                <Link to="/stripe-cards" className="my-stripe-link">
                  <CreditCard className="h-4 w-4" />
                  {t('nav.myStripe')}
                  {hasStripeIssue && (
                    <AlertCircle className="h-4 w-4 ml-1 text-red-600" aria-label={stripeIssueMessage ?? ''} />
                  )}
                </Link>
                )}
                {hasStripeIssue && (
                  <div className="text-xs text-red-600 mt-1 leading-tight">
                    {stripeIssueMessage}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Selected owner indicator for admin/partner (only when viewing another owner) */}
          {(isAdmin || isPartner) && selectedOwner && selectedOwner.id !== user?.id && (
            <div className="selected-owner-banner">
              <div className="selected-owner-info">
                <span className="selected-owner-label">{t('nav.viewingAsOwner', 'Viewing as owner:')}</span>
                <span className="selected-owner-name">
                  {selectedOwner.firstName} {selectedOwner.lastName}
                </span>
                <span className="selected-owner-email">{selectedOwner.email}</span>
              </div>
              <button
                className="selected-owner-clear"
                onClick={() => {
                  if (user) {
                    setSelectedOwner({
                      id: user.id,
                      email: user.email,
                      firstName: user.firstName,
                      lastName: user.lastName,
                    });
                  }
                }}
                title={t('nav.clearSelectedOwner', 'Clear selected owner')}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}

          <nav className="sidebar-nav">
            {/* Owner/Admin sections */}
            {showSidebar && (<>
            {/* Dashboard - top level */}
            {isPageVisible('nav.dashboard') && (
              <Link
                to="/"
                className={cn('nav-item', location.pathname === '/' && 'active')}
              >
                <Home className="h-5 w-5" />
                <span className="nav-label">{t('nav.dashboard')}</span>
              </Link>
            )}

            {/* Company Section */}
            {visibleCompany.length > 0 && (
              <>
                <div
                  className="nav-section-header"
                  onClick={toggleCompany}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <span>{t('nav.sections.company')}</span>
                  {companyExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </div>
                {companyExpanded && visibleCompany.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={cn('nav-item', location.pathname === item.href && 'active')}
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="nav-label">{t(item.nameKey)}</span>
                  </Link>
                ))}
              </>
            )}

            {/* Billing Section */}
            {visibleBilling.length > 0 && (
              <>
                <div
                  className="nav-section-header"
                  onClick={toggleBilling}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <span>{t('nav.sections.billing')}</span>
                  {billingExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </div>
                {billingExpanded && visibleBilling.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={cn('nav-item', location.pathname === item.href && 'active')}
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="nav-label">{t(item.nameKey)}</span>
                  </Link>
                ))}
              </>
            )}

            {/* Bookings Section — hidden entirely (incl. P2P plugin links) when Bookings is closed */}
            {bookingsVisible && (<>
            <div
              className="nav-section-header"
              onClick={toggleBookings}
              onMouseDown={(e) => e.preventDefault()}
            >
              <span>{t('nav.sections.bookings')}</span>
              {bookingsExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </div>
            {bookingsExpanded && bookingsNav.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={cn('nav-item', location.pathname === item.href && 'active')}
              >
                <item.icon className="h-5 w-5" />
                <span className="nav-label">{t(item.nameKey)}</span>
              </Link>
            ))}
            {bookingsExpanded && isPageVisible('nav.p2pBookings') && p2pPlugins.map((p) => {
              const href = `/p2p-bookings/${p.platformId}`;
              return (
                <Link
                  key={p.platformId}
                  to={href}
                  className={cn('nav-item', location.pathname === href && 'active')}
                >
                  <Calendar className="h-5 w-5" />
                  <span className="nav-label">{p.platformName}</span>
                </Link>
              );
            })}
            </>)}

            {/* Vehicles Section */}
            {visibleVehicles.length > 0 && (<>
            <div
              className="nav-section-header"
              onClick={toggleVehicles}
              onMouseDown={(e) => e.preventDefault()}
            >
              <span>{t('nav.sections.vehicles')}</span>
              {vehiclesExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </div>
            {vehiclesExpanded && visibleVehicles.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={cn('nav-item', location.pathname === item.href && 'active')}
              >
                <item.icon className="h-5 w-5" />
                <span className="nav-label">{t(item.nameKey)}</span>
              </Link>
            ))}
            </>)}

            {/* Users Section */}
            {visibleUsers.length > 0 && (
              <>
                <div
                  className="nav-section-header"
                  onClick={toggleUsers}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <span>{t('nav.sections.users')}</span>
                  {usersExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </div>
                {usersExpanded && visibleUsers.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={cn('nav-item', location.pathname === item.href && 'active')}
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="nav-label">{t(item.nameKey)}</span>
                  </Link>
                ))}
              </>
            )}

            {/* Partnership Section - admin or partner only */}
            {visiblePartnership.length > 0 && (
              <>
                <div
                  className="nav-section-header"
                  onClick={togglePartnership}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <span>{t('nav.sections.partnership')}</span>
                  {partnershipExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </div>
                {partnershipExpanded && visiblePartnership.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={cn('nav-item', location.pathname === item.href && 'active')}
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="nav-label">{t(item.nameKey)}</span>
                  </Link>
                ))}
              </>
            )}

            {/* Payments Section */}
            {visiblePayments.length > 0 && (<>
            <div
              className="nav-section-header"
              onClick={togglePayments}
              onMouseDown={(e) => e.preventDefault()}
            >
              <span>{t('nav.sections.payments')}</span>
              {paymentsExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </div>
            {paymentsExpanded && paymentsNav.filter(i => isPageVisible(i.nameKey)).map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={cn('nav-item', location.pathname === item.href && 'active')}
              >
                <item.icon className="h-5 w-5" />
                <span className="nav-label">{t(item.nameKey)}</span>
              </Link>
            ))}
            {paymentsExpanded && disputesNav.filter(i => isPageVisible(i.nameKey)).map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={cn('nav-item', location.pathname === item.href && 'active')}
              >
                <item.icon className="h-5 w-5" />
                <span className="nav-label">{t(item.nameKey)}</span>
              </Link>
            ))}
            </>)}

            {/* Toll Providers Section - dynamic per owner account */}
            {tollAccounts.length > 0 && (
              <>
                <div
                  className="nav-section-header"
                  onClick={toggleTollProviders}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <span>{t('nav.sections.tollProviders')}</span>
                  {tollProvidersExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </div>
                {tollProvidersExpanded && (
                  <>
                    {isPageVisible('nav.tollTransactions') && (
                    <Link
                      to="/toll-transactions"
                      className={cn('nav-item', location.pathname === '/toll-transactions' && 'active')}
                    >
                      <Receipt className="h-5 w-5" />
                      <span className="nav-label">{t('nav.tollTransactions')}</span>
                    </Link>
                    )}
                    <Link
                      to="/violations"
                      className={cn('nav-item', location.pathname === '/violations' && 'active')}
                    >
                      <AlertTriangle className="h-5 w-5" />
                      <span className="nav-label">{t('nav.violations', 'Violations')}</span>
                    </Link>
                    {isPageVisible('nav.supercharger') && (
                    <Link
                      to="/supercharger"
                      className={cn('nav-item', location.pathname === '/supercharger' && 'active')}
                    >
                      <Zap className="h-5 w-5" />
                      <span className="nav-label">{t('nav.supercharger', 'Supercharger')}</span>
                    </Link>
                    )}
                    {tollAccounts.map((account) => {
                      const label = account.nickname
                        ? `${account.providerName} — ${account.nickname}`
                        : `${account.providerName} (${account.accountNumber || account.login})`;
                      return (
                        <Link
                          key={account.id}
                          to={`/toll-provider/${account.id}`}
                          className={cn('nav-item', location.pathname === `/toll-provider/${account.id}` && 'active')}
                        >
                          <Plug className="h-5 w-5" />
                          <span className="nav-label">{label}</span>
                        </Link>
                      );
                    })}
                  </>
                )}
              </>
            )}

            {/* Reports Section - for admin and owner */}
            {visibleReports.length > 0 && (
              <>
                <div
                  className="nav-section-header"
                  onClick={toggleReports}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <span>{t('nav.sections.reports')}</span>
                  {reportsExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </div>
                {reportsExpanded && visibleReports.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={cn('nav-item', location.pathname === item.href && 'active')}
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="nav-label">{t(item.nameKey)}</span>
                  </Link>
                ))}
              </>
            )}
            </>)}

            {/* Renter nav - flat links, no sections */}
            {!showSidebar && (
              <>
                {renterNav.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={cn('nav-item', location.pathname === item.href && 'active')}
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="nav-label">{t(item.nameKey)}</span>
                  </Link>
                ))}
              </>
            )}

            {/* SMS Section - visible to owners/admins */}
            {showSidebar && visibleSms.length > 0 && (<>
            <div
              className="nav-section-header"
              onClick={toggleSms}
              onMouseDown={(e) => e.preventDefault()}
            >
              <span>{t('nav.sections.sms')}</span>
              {smsExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </div>
            {smsExpanded && visibleSms.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={cn('nav-item', location.pathname === item.href && 'active')}
              >
                <item.icon className="h-5 w-5" />
                <span className="nav-label">{t(item.nameKey)}</span>
              </Link>
            ))}
            </>)}

            {/* Administration Section - matches aa-web visibility rules */}
            {showSidebar && visibleAdministration.length > 0 && (
              <>
                <div
                  className="nav-section-header"
                  onClick={toggleAdmin}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <span>{t('nav.sections.administration')}</span>
                  {adminExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </div>
                {adminExpanded && visibleAdministration.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={cn('nav-item', location.pathname === item.href && 'active')}
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="nav-label">{t(item.nameKey)}</span>
                  </Link>
                ))}
              </>
            )}

          </nav>
        </aside>

        {/* Main Content */}
        <main className="main-content">
          {/* Mobile-only Stripe error banner — visible at top of page content so the warning
              is noticed even when the hamburger menu is closed. */}
          {hasStripeIssue && (
            <div
              role="alert"
              className="md:hidden mx-3 mt-3 flex items-start gap-2 rounded-md border-2 border-red-500 bg-red-50 px-3 py-2 shadow-sm"
            >
              <AlertCircle className="h-5 w-5 flex-shrink-0 text-red-600 mt-0.5" />
              <div className="text-sm font-semibold text-red-700 leading-snug">
                {stripeIssueMessage}
              </div>
            </div>
          )}
          {children}
        </main>
      </div>

      {/* Footer */}
      {/* Footer Pin (independent) */}
      <button
        onClick={toggleFooterPinned}
        className={cn('pin-floating', 'pin-floating-bottom', !footerPinned && 'pin-action pin-action-pin')}
        title={footerPinned ? t('nav.unpinFooter', 'Unpin footer') : t('nav.pinFooter', 'Pin footer')}
      >
        {footerPinned ? <Pin /> : <PinOff />}
      </button>

      <footer className={cn('fixed-footer', !footerPinned && 'footer-unpinned')}>
        <div className="footer-container">
          <div className="footer-left">
            <button
              type="button"
              className="footer-copyright"
              onClick={() => setShowCopyright(true)}
            >
              {t('footer.copyright', { year: currentYear })}
            </button>
            <div className="footer-contact">
              <div className="contact-item">
                <Phone className="h-4 w-4" />
                <span>201-724-6644</span>
              </div>
              <div className="contact-item">
                <Mail className="h-4 w-4" />
                <span>support@myeztoll.com</span>
              </div>
            </div>
            <div className="footer-social">
              <a href="https://www.facebook.com/myeztoll" target="_blank" rel="noopener noreferrer">
                <Facebook className="h-5 w-5" />
              </a>
              <a href="https://www.youtube.com/@myeztoll" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                <Youtube className="h-5 w-5" />
              </a>
              <a href="https://www.instagram.com/myeztoll" target="_blank" rel="noopener noreferrer">
                <Instagram className="h-5 w-5" />
              </a>
              {showSidebar && (
                <Link to="/download" title={t('footer.apps.mobileApp')} aria-label={t('footer.apps.mobileApp')}>
                  <AndroidIcon className="h-5 w-5" />
                </Link>
              )}
              {isPageVisible('nav.guides') && (
              <Link to="/guides" title={t('download.guides.title')} aria-label={t('download.guides.title')}>
                <BookOpen className="h-5 w-5" />
              </Link>
              )}
            </div>
          </div>
          <div className="footer-right">
            <Link to="/terms" className="app-link">
              <span>{t('footer.legal.terms')}</span>
            </Link>
            <Link to="/privacy" className="app-link">
              <span>{t('footer.legal.privacy')}</span>
            </Link>
            {/* Google Play requires the account-deletion page to be reachable without installing
                the app; the footer is how a signed-out visitor finds it. */}
            <Link to="/delete-account" className="app-link">
              <span>{t('footer.legal.deleteAccount', 'Delete account')}</span>
            </Link>
          </div>
        </div>
      </footer>

      {/* Copyright Modal */}
      {showCopyright && (
        <>
          <div className="footer-modal-backdrop" onClick={() => setShowCopyright(false)} />
          <div className="footer-modal" role="dialog" aria-modal="true" aria-labelledby="copyright-title">
            <div className="footer-modal-header">
              <h3 id="copyright-title">{t('footer.ipNoticeTitle', 'Intellectual Property Notice')}</h3>
              <button type="button" className="footer-modal-close" onClick={() => setShowCopyright(false)} aria-label={t('footer.closeNotice', 'Close notice')}>
                &times;
              </button>
            </div>
            <div className="footer-modal-body">
              <p><strong>Copyright (c) 2024-{currentYear} Aegis AO Soft LLC. All rights reserved.</strong></p>
              <p>
                This software and its source code are the exclusive property of Aegis AO Soft LLC. No part of this software, including but not limited to source code,
                documentation, compiled binaries, or derivative works, may be used, reproduced, modified,
                distributed, or transmitted in any form or by any means without the express written permission
                of Aegis AO Soft LLC.
              </p>
              <p>
                Aegis AG Soft LLC and all other users use this software solely under a non-exclusive,
                revocable license granted by Aegis AO Soft LLC. Such license confers no ownership rights
                and may be revoked at any time. All rights not expressly granted are reserved.
              </p>
              <p>
                THE SOFTWARE IS PROVIDED &quot;AS IS&quot;, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT
                NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
                IN NO EVENT SHALL Aegis AO Soft LLC BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
                LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH
                THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
              </p>
              <p>
                {t('footer.unauthorizedUse', 'Unauthorized use, copying, modification, distribution, or any other exploitation of this software is strictly prohibited and may result in severe civil and criminal penalties.')}
              </p>
              <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '12px 0' }} />
              <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
                {hostVersion ? (
                  <>
                    <strong>{t('footer.backend', 'Backend:')}</strong> v{hostVersion.version}
                    {hostVersion.buildTimeUtc ? ` · built ${formatDateTime(hostVersion.buildTimeUtc)}` : ''}
                    {hostVersion.environment ? ` · ${hostVersion.environment}` : ''}
                  </>
                ) : (
                  <>{t('footer.loadingVersion', 'Loading backend version…')}</>
                )}
              </p>
            </div>
          </div>
        </>
      )}

      {/* Mobile Bottom Navigation */}
      <nav className="mobile-bottom-nav">
        {!showSidebar ? (
          /* Renter: only SMS Log and Tolls */
          <>
            {renterNav.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={cn('mobile-bottom-nav-item', location.pathname === item.href && 'active')}
              >
                <item.icon className="h-5 w-5" />
                <span>{t(item.nameKey)}</span>
              </Link>
            ))}
          </>
        ) : (
          /* Owner / Admin: full bottom nav */
          <>
        <Link
          to="/"
          className={cn('mobile-bottom-nav-item', location.pathname === '/' && 'active')}
        >
          <Home className="h-5 w-5" />
          <span>{t('nav.home')}</span>
        </Link>
        {isPageVisible('nav.allVehicles') && (
          <Link
            to="/all-vehicles"
            className={cn('mobile-bottom-nav-item', location.pathname === '/all-vehicles' && 'active')}
          >
            <Car className="h-5 w-5" />
            <span>{t('nav.sections.vehicles')}</span>
          </Link>
        )}
        {isPageVisible('nav.users') && (
          <Link
            to="/users"
            className={cn('mobile-bottom-nav-item', location.pathname === '/users' && 'active')}
          >
            <Users className="h-5 w-5" />
            <span>{t('nav.sections.users')}</span>
          </Link>
        )}
        {isPageVisible('nav.charges') && (
          <Link
            to="/payments/charges"
            className={cn('mobile-bottom-nav-item', (location.pathname.includes('/payments')) && 'active')}
          >
            <Receipt className="h-5 w-5" />
            <span>{t('nav.sections.payments')}</span>
          </Link>
        )}
        {isPageVisible('nav.smsLog') && (
          <Link
            to="/sms-log"
            className={cn('mobile-bottom-nav-item', location.pathname === '/sms-log' && 'active')}
          >
            <MessageSquare className="h-5 w-5" />
            <span>{t('nav.sections.sms')}</span>
          </Link>
        )}
          </>
        )}
      </nav>

      {/* Edit Owner Profile Modal */}
      <EditOwnerProfileModal
        isOpen={showOwnerProfileModal}
        onClose={() => setShowOwnerProfileModal(false)}
        user={user}
      />

      {/* Help drawer — mounted once; the desktop and mobile headers only hold triggers. */}
      <HelpPanel />
    </div>
  );
};
